import { supabase } from '../lib/supabase';

export const submitFeedback = async (feedbackData) => {
  try {
    const { 
      project_id, 
      judge_name, 
      feedback,
      daily_value_score,
      productivity_score,
      mvp_quality_score,
      arabic_focus_score,
      innovation_score,
      scalability_score,
      creativity_score,
      code_quality_score,
      usefulness_score,
      documentation_score,
      sustainability_score,
      is_theme_matched,
      is_ai_used,
      are_commits_consistent,
      score, // This will now be the calculated total
      user_agent
    } = feedbackData;

    // Get IP address
    let ip_address = null;
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      ip_address = data.ip;
    } catch (ipError) {
      console.error('Error getting IP address:', ipError);
    }

    const { data, error } = await supabase
      .from('project_feedback')
      .insert([{
        project_id,
        judge_name,
        score,
        feedback,
        daily_value_score,
        productivity_score,
        mvp_quality_score,
        arabic_focus_score,
        innovation_score,
        scalability_score,
        creativity_score,
        code_quality_score,
        usefulness_score,
        documentation_score,
        sustainability_score,
        is_theme_matched,
        is_ai_used,
        are_commits_consistent,
        user_agent,
        ip_address
      }])
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      message: 'تم إرسال التقييم بنجاح',
      data
    };
  } catch (err) {
    console.error('Error submitting feedback:', err);
    return {
      success: false,
      message: 'حدث خطأ أثناء إرسال التقييم'
    };
  }
};

export const getProjectFeedback = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from('project_feedback')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data
    };
  } catch (err) {
    console.error('Error fetching feedback:', err);
    return {
      success: false,
      message: 'حدث خطأ أثناء جلب التقييمات',
      data: []
    };
  }
};

export const getAllProjectsWithScores = async () => {
  try {
    // Get all projects
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select(`
        *,
        team:teams!projects_team_id_fkey(
          team_name,
          leader:applicants!teams_leader_id_fkey(full_name, email)
        )
      `)
      .eq('submitted', true);

    if (projectsError) throw projectsError;

    // Get all feedback
    const { data: feedback, error: feedbackError } = await supabase
      .from('project_feedback')
      .select('*');

    if (feedbackError) throw feedbackError;

    // Calculate scores and screening status
    const projectsWithScores = projects.map(project => {
      const allProjectFeedback = feedback.filter(f => f.project_id === project.id);
      
      // Deduplicate feedback by judge_name (take the latest one)
      // Assuming created_at is available and sortable.
      // If no created_at, we might have issues, but supabase adds it.
      allProjectFeedback.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      const uniqueFeedbackMap = new Map();
      allProjectFeedback.forEach(f => {
        if (!uniqueFeedbackMap.has(f.judge_name)) {
          uniqueFeedbackMap.set(f.judge_name, f);
        }
      });
      
      const projectFeedback = Array.from(uniqueFeedbackMap.values());

      // Screening Logic
      const themeMatchVotes = projectFeedback.filter(f => f.is_theme_matched === true).length;
      const themeMismatchVotes = projectFeedback.filter(f => f.is_theme_matched === false).length; // assuming explicit false vs null
      // Check if feedback actually has is_theme_matched set (it defaults to false in schema, so we rely on explicit submission)
      // Actually schema says default false. So we should check if they voted. 
      // But wait, if they submit a full evaluation, is_theme_matched is part of it.
      // If they submit screening only, is_theme_matched is part of it.
      
      let screeningStatus = 'pending';
      if (themeMatchVotes >= 3) {
        screeningStatus = 'qualified';
      } else if (themeMismatchVotes >= 3) {
        screeningStatus = 'disqualified';
      }

      // Scoring Logic (only count feedback that has a score)
      const scoredFeedback = projectFeedback.filter(f => f.score !== null && f.score !== undefined);
      const totalScore = scoredFeedback.reduce((sum, f) => sum + (f.score || 0), 0);
      const averageScore = scoredFeedback.length > 0 ? totalScore / scoredFeedback.length : 0;

      // Disqualify if 3 or more judges rated 50 or below
      const judgesRatedLow = scoredFeedback.filter(f => f.score <= 50).length;
      if (screeningStatus === 'qualified' && judgesRatedLow >= 3) {
        screeningStatus = 'disqualified';
      }

      return {
        ...project,
        averageScore,
        feedbackCount: scoredFeedback.length, // Count only scored feedbacks for "Evaluations" count
        screeningVotes: projectFeedback.length, // Total votes including screening
        feedbackList: projectFeedback,
        screeningStatus,
        themeMatchVotes,
        themeMismatchVotes
      };
    });

    // Sort by average score descending
    projectsWithScores.sort((a, b) => b.averageScore - a.averageScore);

    return {
      success: true,
      data: projectsWithScores
    };
  } catch (err) {
    console.error('Error fetching projects with scores:', err);
    return {
      success: false,
      message: 'حدث خطأ أثناء جلب المشاريع',
      data: []
    };
  }
};

